<template>
  <div class="field box py-1">
    <div class="field-label is-normal is-flex is-justify-content-space-between is-align-items-center m-0 mb-3 is-flex-direction-row-reverse">
      <button v-if="submitableButton && edit && !isLoading" class="button is-info" @click="handleChange">Submit</button>
      <label class="label">
        {{ label }}
        <span class="tag is-info is-small ml-2">Mixed</span>
        <span v-if="showNotDisplayed && isLiteMode" class="red_text">&nbsp;</span>
      </label>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="loading-container">
      <div class="is-flex is-align-items-center is-justify-content-center">
        <div class="loading-spinner"></div>
        <span class="loading-text">טוען אבסטרקט מעורב...</span>
      </div>
    </div>

    <!-- Edit Mode -->
    <div v-else-if="edit" class="control ql-container-custom">
      <quill v-model="text" output="html" :config="quillConfig" ref="quillEditor" @input="handleTextChange" />
    </div>

    <!-- Display Mode -->
    <div v-else @click="updateEdit">
      <div dir="rtl" v-if="text && text.trim().length > 0">
        <div class="is-clickable white_space_pre_line break_word has-text-link" v-html="$sanitize(text)"></div>
      </div>
      <div v-else class="is-clickable has-text-link">
        {{ placeholderText }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MixedAbstract',
  props: {
    applicantId: {
      type: Number,
      required: true
    },
    offerId: {
      type: Number,
      required: true
    },
    label: {
      type: String,
      default: 'Mixed Abstract'
    },
    submitableButton: {
      type: Boolean,
      default: true
    },
    isEditable: {
      type: Boolean,
      default: false
    },
    showNotDisplayed: {
      type: Boolean,
      default: false
    },
    isLiteMode: {
      type: Boolean,
      default: false
    },
    placeholderText: {
      type: String,
      default: 'Create...'
    }
  },
  data () {
    return {
      edit: false,
      text: '',
      abstractId: null,
      isLoading: false,
      quillConfig: {
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            [{ direction: 'rtl' }]
          ]
        }
      }
    }
  },
  watch: {
    text (newVal) {
      this.$emit('input', newVal)
    }
  },
  created () {
    this.edit = this.isEditable
    this.addQuillStyling()
    this.loadMixedAbstract()
  },
  mounted () {
    this.$nextTick(() => {
      if (this.$refs.quillEditor && this.$refs.quillEditor.$el) {
        this.setDefaultAlignRight()
      }
    })
  },
  beforeDestroy () {
    this.detachQuillBlurEvent()
  },
  methods: {
    addQuillStyling () {
      var link = document.createElement('link')
      link.setAttribute('rel', 'stylesheet')
      link.setAttribute('href', 'https://cdn.quilljs.com/1.2.6/quill.snow.css')
      document.body.appendChild(link)
    },
    updateEdit () {
      this.edit = true
      this.$nextTick(() => {
        if (this.$refs.quillEditor && this.$refs.quillEditor.$el) {
          this.setDefaultAlignRight()
          // Set the content in the Quill editor
          if (this.$refs.quillEditor.editor && this.text) {
            this.$refs.quillEditor.editor.root.innerHTML = this.text
          }
        }
      })
    },
    setDefaultAlignRight () {
      try {
        const pElement = this.$refs.quillEditor.$el.querySelector('p')
        const containerElement = this.$refs.quillEditor.$el.querySelector('.ql-container')

        if (pElement) {
          pElement.classList.add('ql-align-right')
          pElement.classList.add('ql-direction-rtl')
        }
        if (containerElement) {
          containerElement.classList.add('ql-container-custom')
        }
      } catch (error) {
        console.warn('Could not set RTL alignment:', error)
      }
    },
    attachQuillBlurEvent () {
      const quillEditor = this.$refs.quillEditor && this.$refs.quillEditor.editor
      if (quillEditor) {
        quillEditor.on('selection-change', (range) => {
          if (range === null) {
            this.handleChange()
          }
        })
      }
    },
    detachQuillBlurEvent () {
      const quillEditor = this.$refs.quillEditor && this.$refs.quillEditor.editor
      if (quillEditor) {
        quillEditor.off('selection-change')
      }
    },
    async handleTextChange (newText) {
      this.text = newText
      this.$emit('input', newText)

      // Auto-save mixed abstract
      const trimmedText = newText ? newText.trim() : ''
      if (trimmedText.length === 0) {
        return // Don't save empty content
      }

      await this.saveMixedAbstract(newText)
    },
    async saveMixedAbstract (text) {
      try {
        const trimmedText = text ? text.trim() : ''
        if (trimmedText.length === 0) {
          console.log('Skipping save of empty mixed abstract')
          return
        }

        if (this.abstractId) {
          // Update existing abstract
          await this.$caller.request(
            'put',
            `/applicant_abstracts/${this.abstractId}`,
            'admin_headers',
            {
              content: text
            }
          )
        } else {
          // Create new abstract
          const response = await this.$caller.request(
            'post',
            '/applicant_abstracts',
            'admin_headers',
            {
              applicant_id: this.applicantId,
              content: text,
              version: 1
            }
          )

          if (response && response.id) {
            this.abstractId = response.id
            // Emit the new abstract ID to parent component
            this.$emit('abstractIdChanged', this.abstractId)
          }
        }
      } catch (error) {
        console.error('Failed to save mixed abstract:', error)
        this.$utils.emit('show_toast', { text: 'שגיאה בשמירת האבסטרקט המעורב' })
      }
    },
    async loadMixedAbstract () {
      this.isLoading = true
      try {
        const response = await this.$caller.request(
          'get',
          `/abstract_picker/get_mixed?offer_id=${this.offerId}`,
          'admin_headers'
        )

        if (response && response.abstract && response.abstract.trim().length > 0) {
          this.text = response.abstract
          this.abstractId = response.id
          this.$emit('input', this.text)
          // Emit the abstract ID to parent component
          this.$emit('abstractIdChanged', this.abstractId)
        } else {
          // Initialize with empty content
          this.text = ''
          this.abstractId = null
          this.$emit('input', this.text)
          // Emit null ID to parent component
          this.$emit('abstractIdChanged', this.abstractId)
        }
      } catch (error) {
        console.error('Error loading mixed abstract:', error)
        this.text = ''
        this.abstractId = null
        this.$emit('input', this.text)
        this.$utils.emit('show_toast', { text: 'שגיאה בטעינת האבסטרקט המעורב' })
      } finally {
        this.isLoading = false
      }
    },
    async handleChange () {
      this.edit = false
      this.$utils.emit('show_toast', { text: 'Success' })
    }
  }
}
</script>

<style scoped>
.ql-container-custom {
  font-size: 17px !important;
}

.red_text {
  color: #ff3860;
}

.tag.is-info.is-small {
  font-size: 0.65rem;
  height: 1.5em;
}

.loading-container {
  padding: 20px;
  text-align: center;
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  margin: 10px 0;
}

.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-left: 10px;
}

.loading-text {
  color: #6c757d;
  font-size: 14px;
  font-weight: 500;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
